<script setup>
import { ref, nextTick, watch, computed } from 'vue';
import AiChatMessage from './AiChatMessage.vue';
import AiChatInput from './AiChatInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  agent: { type: Object, required: true },
  messages: { type: Array, default: () => [] },
  conversation: { type: Object, default: null },
  accountAgents: { type: Array, default: () => [] },
  isSending: { type: Boolean, default: false },
  isStreaming: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  hasConversation: { type: Boolean, default: false },
});

const emit = defineEmits([
  'send-message',
  'new-conversation',
  'delete-conversation',
  'add-participant',
  'remove-participant',
  'back',
]);

const messagesContainer = ref(null);
const showParticipantsMenu = ref(false);
const showDeleteModal = ref(false);
const participantSearch = ref('');

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop =
        messagesContainer.value.scrollHeight;
    }
  });
};

watch(
  () => [props.messages.length, props.messages[props.messages.length - 1]?.content],
  () => scrollToBottom(),
  { deep: false }
);

let streamScrollInterval = null;
watch(
  () => props.isStreaming,
  streaming => {
    if (streaming) {
      streamScrollInterval = setInterval(scrollToBottom, 100);
    } else {
      clearInterval(streamScrollInterval);
      scrollToBottom();
    }
  }
);

const agentInitials = computed(() => {
  return props.agent.name
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
});

const isInputDisabled = computed(
  () => props.isSending || props.isStreaming
);

const participantIds = computed(() => {
  return (props.conversation?.participants || []).map(p => p.id);
});

const filteredAgents = computed(() => {
  const search = participantSearch.value.toLowerCase();
  return (props.accountAgents || []).filter(a => {
    if (participantIds.value.includes(a.id)) return false;
    if (!search) return true;
    return (a.name || '').toLowerCase().includes(search);
  });
});

const handleSend = content => {
  emit('send-message', content);
};

const confirmDelete = () => {
  emit('delete-conversation');
  showDeleteModal.value = false;
};

const closeParticipantsMenu = () => {
  showParticipantsMenu.value = false;
  participantSearch.value = '';
};
</script>

<template>
  <div class="flex h-full flex-col">
    <div
      class="flex items-center gap-3 border-b border-n-weak px-4 py-3 backdrop-blur-sm"
    >
      <button class="md:hidden" @click="emit('back')">
        <span class="i-lucide-arrow-left text-lg text-n-slate-11" />
      </button>
      <div
        class="flex size-9 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-sm font-bold text-white"
      >
        {{ agentInitials }}
      </div>
      <div class="flex flex-1 flex-col">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ agent.name }}
        </h3>
        <span
          v-if="isStreaming"
          class="text-xs text-violet-500"
        >
          Digitando...
        </span>
        <span
          v-else-if="agent.description"
          class="truncate text-xs text-n-slate-10"
        >
          {{ agent.description }}
        </span>
      </div>

      <div
        v-if="hasConversation && conversation?.participants?.length"
        class="flex -space-x-1.5"
      >
        <div
          v-for="p in conversation.participants.slice(0, 4)"
          :key="p.id"
          class="flex size-7 items-center justify-center rounded-full border-2 border-n-background bg-n-alpha-3 text-[10px] font-semibold text-n-slate-11"
          :title="p.name"
        >
          {{ (p.name || '?').charAt(0).toUpperCase() }}
        </div>
        <div
          v-if="conversation.participants.length > 4"
          class="flex size-7 items-center justify-center rounded-full border-2 border-n-background bg-n-alpha-3 text-[10px] font-semibold text-n-slate-11"
        >
          +{{ conversation.participants.length - 4 }}
        </div>
      </div>

      <div v-if="hasConversation" class="flex items-center gap-0.5">
        <button
          class="flex size-9 items-center justify-center rounded-xl text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
          title="Convidar participantes"
          @click="showParticipantsMenu = !showParticipantsMenu"
        >
          <span class="i-lucide-user-plus text-[18px]" />
        </button>

        <button
          class="flex size-9 items-center justify-center rounded-xl text-n-slate-10 transition-colors hover:bg-red-500/10 hover:text-red-500"
          title="Excluir conversa"
          @click="showDeleteModal = true"
        >
          <span class="i-lucide-trash-2 text-[18px]" />
        </button>

        <button
          class="flex size-9 items-center justify-center rounded-xl text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
          title="Nova conversa"
          @click="emit('new-conversation')"
        >
          <span class="i-lucide-plus text-[18px]" />
        </button>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="showDeleteModal"
        class="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 backdrop-blur-sm"
        @click.self="showDeleteModal = false"
      >
        <div
          class="w-80 rounded-2xl border border-n-weak bg-n-background p-6 shadow-xl"
        >
          <div class="mb-4 flex items-center gap-3">
            <div
              class="flex size-10 items-center justify-center rounded-xl bg-red-500/10"
            >
              <span class="i-lucide-trash-2 text-lg text-red-500" />
            </div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              Excluir conversa
            </h3>
          </div>
          <p class="mb-5 text-sm text-n-slate-11">
            Esta ação é irreversível. Todas as mensagens desta conversa serão
            excluídas permanentemente.
          </p>
          <div class="flex gap-3">
            <button
              class="flex-1 rounded-xl bg-n-alpha-2 px-4 py-2.5 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-3"
              @click="showDeleteModal = false"
            >
              Cancelar
            </button>
            <button
              class="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700"
              @click="confirmDelete"
            >
              Excluir
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="showParticipantsMenu"
        class="fixed inset-0 z-[9998]"
        @click="closeParticipantsMenu"
      />
      <div
        v-if="showParticipantsMenu"
        class="fixed right-20 top-16 z-[9999] w-72 rounded-xl border border-n-weak bg-n-background shadow-xl"
        @click.stop
      >
        <div class="border-b border-n-weak px-3 py-2">
          <h4 class="text-xs font-semibold text-n-slate-12">
            Participantes
          </h4>
        </div>

        <div
          v-if="conversation?.participants?.length"
          class="max-h-32 overflow-y-auto border-b border-n-weak"
        >
          <div
            v-for="p in conversation.participants"
            :key="p.id"
            class="flex items-center justify-between px-3 py-1.5"
          >
            <div class="flex items-center gap-2">
              <div
                class="flex size-6 items-center justify-center rounded-full bg-n-alpha-3 text-[10px] font-semibold text-n-slate-11"
              >
                {{ (p.name || '?').charAt(0).toUpperCase() }}
              </div>
              <span class="text-xs text-n-slate-12">{{ p.name }}</span>
            </div>
            <button
              class="rounded p-1 text-n-slate-9 hover:bg-n-alpha-2 hover:text-red-500"
              @click="emit('remove-participant', p.id)"
            >
              <span class="i-lucide-x text-sm" />
            </button>
          </div>
        </div>

        <div class="p-2">
          <input
            v-model="participantSearch"
            placeholder="Buscar agente..."
            class="reset-base mb-1.5 w-full rounded-lg !border-0 bg-n-alpha-2 px-2.5 py-1.5 text-xs text-n-slate-12 !outline-none !ring-0 placeholder:text-n-slate-9"
          />
          <div class="max-h-36 overflow-y-auto">
            <button
              v-for="a in filteredAgents.slice(0, 10)"
              :key="a.id"
              class="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left hover:bg-n-alpha-2"
              @click="emit('add-participant', a.id)"
            >
              <div
                class="flex size-6 items-center justify-center rounded-full bg-violet-500/10 text-[10px] font-semibold text-violet-600"
              >
                {{ (a.name || '?').charAt(0).toUpperCase() }}
              </div>
              <span class="text-xs text-n-slate-12">{{ a.name }}</span>
            </button>
            <p
              v-if="filteredAgents.length === 0"
              class="px-2.5 py-2 text-center text-[10px] text-n-slate-9"
            >
              Nenhum agente encontrado
            </p>
          </div>
        </div>
      </div>
    </Teleport>

    <div
      v-if="!hasConversation"
      class="flex flex-1 flex-col items-center justify-center gap-6 px-8"
    >
      <div
        class="flex size-20 items-center justify-center rounded-3xl bg-gradient-to-br from-violet-500/10 to-indigo-600/10"
      >
        <div
          class="flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 text-xl font-bold text-white"
        >
          {{ agentInitials }}
        </div>
      </div>
      <div class="text-center">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ agent.name }}
        </h2>
        <p
          v-if="agent.description"
          class="mt-1 max-w-md text-sm text-n-slate-10"
        >
          {{ agent.description }}
        </p>
      </div>
      <Button
        size="md"
        variant="solid"
        color-scheme="primary"
        icon="i-lucide-message-circle-plus"
        label="Iniciar conversa"
        @click="emit('new-conversation')"
      />
    </div>

    <template v-else>
      <div
        ref="messagesContainer"
        class="flex-1 overflow-y-auto px-4 py-4"
      >
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-12"
        >
          <span
            class="i-lucide-loader-2 animate-spin text-2xl text-n-slate-10"
          />
        </div>

        <div
          v-else-if="messages.length === 0 && !isSending"
          class="flex flex-col items-center justify-center py-16 text-center"
        >
          <div
            class="mb-4 flex size-12 items-center justify-center rounded-2xl bg-n-alpha-1"
          >
            <span
              class="i-lucide-message-square text-xl text-n-slate-10"
            />
          </div>
          <p class="text-sm text-n-slate-10">
            Envie uma mensagem para começar.
          </p>
        </div>

        <div v-else class="mx-auto flex max-w-3xl flex-col gap-4">
          <AiChatMessage
            v-for="message in messages"
            :key="message.id"
            :message="message"
            :agent-name="agent.name"
            :agent-initials="agentInitials"
            :is-streaming="isStreaming && message === messages[messages.length - 1] && message.role === 'assistant'"
          />

          <div
            v-if="isSending && !isStreaming"
            class="flex items-start gap-3"
          >
            <div
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-xs font-bold text-white"
            >
              {{ agentInitials }}
            </div>
            <div
              class="rounded-2xl rounded-tl-sm bg-n-alpha-2 px-4 py-3"
            >
              <div class="flex items-center gap-1">
                <span
                  class="size-2 animate-bounce rounded-full bg-n-slate-9"
                  style="animation-delay: 0ms"
                />
                <span
                  class="size-2 animate-bounce rounded-full bg-n-slate-9"
                  style="animation-delay: 150ms"
                />
                <span
                  class="size-2 animate-bounce rounded-full bg-n-slate-9"
                  style="animation-delay: 300ms"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AiChatInput
        :is-sending="isInputDisabled"
        @send="handleSend"
      />
    </template>
  </div>
</template>
